/**
 * src/pages/WhereDidWeComeFrom/sections/OriginOfLifeSection.tsx
 * Section 4: "How life may have begun"
 * Honest epistemological ExpandList of origin-of-life hypotheses
 * clearly distinguishing what we know from what remains unresolved.
 */

import React from "react";
import { ExpandList } from "@/components/originBeyond/ExpandList";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { DisclaimerNote } from "@/components/originBeyond/DisclaimerNote";
import { ORIGIN_OF_LIFE_HYPOTHESES } from "../whereDidWeComeFromData";
import "@/components/cosmic/cosmic.css";
export function OriginOfLifeSection() {
  return <section id="origin-of-life" className="w-full max-w-[76rem] mx-auto px-4 sm:px-6 lg:px-8 min-w-0" aria-label="Origin of Life Hypotheses">
      <SectionHeading eyebrow="Prebiotic Chemistry" title="How Life May Have Begun: From Geochemistry to Biology" description="Life arose on Earth surprisingly early—within its first billion years. Yet how inanimate organic matter crossed the threshold into self-sustaining, self-replicating cells remains an active scientific question." />

      {/* What we know vs what we don't panel */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
        <div className="ql-glass p-5 sm:p-6 border border-primary/30 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-primary shadow-[0_0_8px_var(--glow-primary)]" />
            <h3 className="font-serif text-base sm:text-lg font-bold text-foreground">
              What We Empirically Know
            </h3>
          </div>
          <ul className="text-xs sm:text-sm text-muted-foreground space-y-2 font-sans list-disc list-inside mt-2">
            <li>
              Organic molecules (amino acids, sugars, nitrogenous bases) form readily in interstellar clouds, comets, and primitive planetary atmospheres.
            </li>
            <li>
              Microbial fossil and isotopic evidence proves cellular life flourished on Earth by at least 3.5 billion years ago.
            </li>
            <li>
              All modern terrestrial organisms share a universal genetic code, ATP energetic machinery, and homologous ribosomal architecture.
            </li>
          </ul>
        </div>

        <div className="ql-glass p-5 sm:p-6 border border-border/50 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="size-2 rounded-full bg-amber-400 shadow-[0_0_8px_hsl(38,95%,50%)]" />
            <h3 className="font-serif text-base sm:text-lg font-bold text-foreground">
              What Remains Unresolved
            </h3>
          </div>
          <ul className="text-xs sm:text-sm text-muted-foreground space-y-2 font-sans list-disc list-inside mt-2">
            <li>
              The precise physical environment where the first self-replicating metabolic polymer network coalesced.
            </li>
            <li>
              How homochirality (life&apos;s exclusive preference for left-handed amino acids and right-handed sugars) was initially selected.
            </li>
            <li>
              How the transition occurred from mineral-catalyzed reactions to enclosed, membrane-bounded autonomous cells.
            </li>
          </ul>
        </div>
      </div>

      {/* ExpandList with Hypotheses */}
      <ExpandList items={ORIGIN_OF_LIFE_HYPOTHESES} defaultOpenId="vent-hypothesis" className="mb-8" />

      <DisclaimerNote tone="science" title="Epistemic Transparency">
        None of these hypotheses are fully established fact. Each offers compelling experimental evidence for individual steps (organic synthesis, catalytic polymers, compartmentalization), but unifying them into an empirically verified pathway remains one of modern science&apos;s grand open frontiers.
      </DisclaimerNote>
    </section>;
}